import React, { useCallback, useEffect, useMemo, useState } from "react";
import HeaderSection from "./component/HeaderSection";
import { useGetSectionsQuery } from "../../redux/features/cache";
import GridWrapper from "../grid/GridWrapper";
import { State, process } from "@progress/kendo-data-query";
import {
  GridCellProps,
  GridColumn,
  GridContextMenuEvent,
  GridDetailRowProps,
  GridExpandChangeEvent,
  GridRowClickEvent,
  GridToolbar,
} from "@progress/kendo-react-grid";
import { sectionHeader } from "../../constante/sectionHeaders";
import {
  Box,
  Button,
  Chip,
  ClickAwayListener,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import DetailComponent from "./component/DetailComponent";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { planoAPI } from "../../redux/features/cache";
import GenericModal from "../planogramme/modals/GenericModal";
import { getModalStyle } from "../modalStyle";
import WrapperModification from "./component/WrapperModification";
import { Section } from "../../models/Section";
import FormNewSection from "./component/FormNewSection";
import { useSearchParams } from "react-router-dom";
import sizeConfigs from "../../configs/sizeConfigs";
import CustomGrid from "../grid/CustomGrid";
import { UrlParams } from "../../constante/UrlParams";
import useUrlParamsHandler from "../../hooks/useUrlParamsHandler";
import { mapTree } from "@progress/kendo-react-common";

function Sections() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlParamsHandler = useUrlParamsHandler();
  const [user, setUser] = useState<string>(
    urlParamsHandler.getParams(UrlParams.Acheteur)
  );
  const [div, setDiv] = useState<string>(
    urlParamsHandler.getParams(UrlParams.Division)
  );
  const [openNewProduit, setOpenNewProduit] = useState<boolean>(false);
  const handleCloseNewProduit = () => {
    setOpenNewProduit(false);
  };
  const dispatch = useAppDispatch();
  const [inputValue, setInputValue] = useState("");
  const { data: sections = [] } = useGetSectionsQuery({
    div,
    simple: false,
    user,
    rech: inputValue,
    treeView: false,
  });
  const [selectedRow, setSelectedRow] = useState<Section>();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const [newSection, setNewSection] = useState<Section | null>(null);
  const [collapsedGroups, setCollapsedGroups] = React.useState<string[]>([]);
  const handleClose = () => {
    setOpen(false);
  };
  const openMod = Boolean(anchorEl);
  const initialDataState: State = {
    group: [{ field: "division.div_nom" }],
  };
  const [dataState] = useState<State>(initialDataState);
  const [dataResult, setDataResult] = React.useState(
    process(sections, dataState)
  );
  const sectionHeaderExpanded = useAppSelector(
    (state) => state.appState.sectionHeaderExpanded
  );
  const d_inv026 = useAppSelector((state) => state.appState.d_inv026);

  const getTextWidth = (text: string, font: string) => {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    context!.font = font;

    return context?.measureText(text).width;
  };

  const calculateColumnWidth = useCallback(
    (sections: any[], field: string, label: string) => {
      let maxWidth = 0;

      sections.forEach((item) => {
        const sizeField = getTextWidth(item[field], "Roboto");
        const sizeLabel = getTextWidth(item[label], "Roboto");
        const width = Math.max(sizeField!, sizeLabel!) + 100;

        if (width > maxWidth) {
          maxWidth = width;
        }
      });
      return maxWidth;
    },
    []
  );

  useEffect(() => {
    if (newSection !== null) {
      setSelectedRow(newSection);
      setOpenNewProduit(false);
      setOpen(true);
    }
  }, [newSection]);

  useEffect(() => {
    var search = new URLSearchParams(window.location.href.split("?")[1]);
    if (search.get("id") !== null) {
      if (dataResult.data.length > 0) {
        let id = search.get("id");
        let sectionToOpen = sections.find(
          (e) => e.plans_seq === parseInt(id!)
        )?.plans_nom;
        document.querySelectorAll(".k-table-td").forEach((x) => {
          if (x.textContent === sectionToOpen) {
            x.scrollIntoView();
          }
        });
        let i = sections.findIndex((e) => e.plans_seq === parseInt(id!));
        if (i !== -1) {
          dispatch(
            planoAPI.util.updateQueryData(
              "getSections",
              {
                div: div,
                simple: false,
                user,
                rech: inputValue,
                treeView: false,
              },
              (section) => {
                section.forEach((e) => (e.selected = false));
                section[i].selected = section[i].selected = true;
                section[i].expanded = section[i].expanded = true;
              }
            )
          );
          searchParams.delete("id");
          setSearchParams(searchParams);
        }
      }
    }
  }, [dataResult]);

  useEffect(() => {
    let section = sections.find((e) => e.plans_seq === selectedRow?.plans_seq);
    if (section) {
      setSelectedRow(section);
    }
    setDataResult(process(sections, dataState));
  }, [sections]);

  const expandChange = useCallback(
    (event: GridExpandChangeEvent) => {
      if (event.dataIndex !== -1) {
        let i = sections.findIndex(
          (e) => e.plans_seq === event.dataItem.plans_seq
        );
        dispatch(
          planoAPI.util.updateQueryData(
            "getSections",
            {
              div: div,
              simple: false,
              user,
              rech: inputValue,
              treeView: false,
            },
            (section) => {
              section[i].expanded =
                section[i].expanded === undefined ? true : !section[i].expanded;
              return section;
            }
          )
        );
      } else {
        let groupId = event.dataItem.field + "_" + event.dataItem.value;
        if (!collapsedGroups.includes(groupId)) {
          setCollapsedGroups([...collapsedGroups, groupId]);
        } else {
          setCollapsedGroups(collapsedGroups.filter((gr) => gr !== groupId));
        }
      }
    },
    [dispatch, collapsedGroups, div, inputValue, sections, user]
  );

  const handleContextMenu = useCallback(
    (event: GridContextMenuEvent) => {
      let i = sections.findIndex(
        (e) => e.plans_seq === event.dataItem.plans_seq
      );
      dispatch(
        planoAPI.util.updateQueryData(
          "getSections",
          { div: div, simple: false, user, rech: inputValue, treeView: false },
          (section) => {
            section.forEach((e) => (e.selected = false));
            section[i].selected = section[i].selected = true;
          }
        )
      );
      setSelectedRow(event.dataItem);
      setAnchorEl(event.nativeEvent.target);
    },
    [dispatch, div, inputValue, sections, user]
  );

  const onContextMenu = useCallback(
    (event: GridContextMenuEvent) => {
      event.nativeEvent.preventDefault();
      handleContextMenu(event);
    },
    [handleContextMenu]
  );

  const handleCloseMod = () => {
    setAnchorEl(null);
  };

  const handleRowClick = useCallback(
    (event: GridRowClickEvent) => {
      let i = sections.findIndex(
        (e) => e.plans_seq === event.dataItem.plans_seq
      );
      dispatch(
        planoAPI.util.updateQueryData(
          "getSections",
          { div: div, simple: false, user, rech: inputValue, treeView: false },
          (section) => {
            section.forEach((e) => (e.selected = false));
            section[i].selected = section[i].selected = true;
          }
        )
      );
    },
    [dispatch, div, inputValue, sections, user]
  );

  const AcheteurCell = (props: any) => {
    if (props.rowType === "groupHeader") return null;
    return (
      <td>
        {props.dataItem.secAcheteur &&
          props.dataItem.secAcheteur.map((e: any) => (
            <Chip label={e.usr_nom} sx={{ m: 0.5 }} />
          ))}
      </td>
    );
  };

  const DetailComponentWrapper = useCallback(
    (gridDetailRowProps: GridDetailRowProps) => {
      return (
        <DetailComponent
          plans_seq={gridDetailRowProps.dataItem.plans_seq}
        ></DetailComponent>
      );
    },
    []
  );

  const MyAcheteurCell = useCallback(
    (props: GridCellProps) => <AcheteurCell {...props} />,
    []
  );

  const generateGridRows = useCallback(
    (sections: any[]) => {
      return sectionHeader.map((item, index) => {
        const columnWidth =
          sectionHeader.length - 1 !== index
            ? calculateColumnWidth(sections, item.champ, item.label_champ)
            : undefined;

        return (
          <GridColumn
            field={item.champ}
            key={item.label_champ}
            title={item.label_champ}
            format={item.format_champ}
            headerClassName={`${
              item.format_champ === "{0:c}" ? "alignRight" : ""
            }`}
            className={`${item.format_champ === "{0:c}" ? "alignRight" : ""}`}
            cell={item.label_champ === "Acheteur" ? MyAcheteurCell : undefined}
            width={columnWidth}
          />
        );
      });
    },
    [MyAcheteurCell, calculateColumnWidth]
  );

  const handleGroupState = useCallback(
    (props: any) => {
      return {
        data: mapTree(props.data, "items", (group) => {
          if (!group.aggregates) return group;
          let groupId = group.field + "_" + group.value;
          return { ...group, expanded: !collapsedGroups.includes(groupId) };
        }),
        total: props.total,
      };
    },
    [collapsedGroups]
  );

  const grid = useMemo(() => {
    const generatedGridRows = generateGridRows(sections);
    const toolbar = (
      <GridToolbar className="icon-toolbar">
        <Button
          variant="contained"
          onClick={() => setOpenNewProduit(true)}
          sx={{
            width: "300px",
            borderRadius: "30px",
            m: 2,
            backgroundColor: "#007bff",
            position: "absolute",
            right: 0,
          }}
          disabled={!d_inv026.canAdd}
        >
          Ajouter une nouvelle section
        </Button>
      </GridToolbar>
    );

    return (
      <GridWrapper>
        <CustomGrid
          data={handleGroupState(dataResult)}
          dataState={dataState}
          handleClick={handleRowClick}
          expandChange={expandChange}
          detail={DetailComponentWrapper}
          onContextMenu={onContextMenu}
          rows={generatedGridRows}
          toolbar={toolbar}
          id="plans_seq"
        />
      </GridWrapper>
    );
  }, [
    generateGridRows,
    sections,
    d_inv026.canAdd,
    dataResult,
    dataState,
    handleGroupState,
    handleRowClick,
    expandChange,
    DetailComponentWrapper,
    onContextMenu,
  ]);

  return (
    <div>
      <GenericModal
        open={open}
        handleClose={handleClose}
        title="Modification de la section"
        style={getModalStyle}
      >
        <WrapperModification section={selectedRow} handleClose={handleClose} />
      </GenericModal>
      <GenericModal
        open={openNewProduit}
        handleClose={handleCloseNewProduit}
        style={getModalStyle}
        title="Ajouter une nouvelle section"
      >
        <FormNewSection setNewSection={setNewSection} />
      </GenericModal>
      <ClickAwayListener onClickAway={handleCloseMod}>
        <Menu
          id="demo-positioned-menu"
          aria-labelledby="demo-positioned-button"
          anchorEl={anchorEl}
          open={openMod}
          onClose={handleCloseMod}
          anchorOrigin={{
            vertical: "center",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "center",
            horizontal: "left",
          }}
        >
          <MenuItem
            disabled={!d_inv026.canModify}
            onClick={() => setOpen(true)}
          >
            Modifier
          </MenuItem>
          <MenuItem onClick={handleCloseMod}>Annuler</MenuItem>
        </Menu>
      </ClickAwayListener>
      <Box
        sx={{
          display: "flex",
          height: `calc(100vh - ${sizeConfigs.topbar.height})`,
        }}
      >
        {sectionHeaderExpanded && (
          <Box
            sx={{
              width: sizeConfigs.sidebar.width,
              minHeight: "50px",
              whiteSpace: "nowrap",
              padding: "10px",
            }}
          >
            <HeaderSection
              edit={false}
              div={div}
              setDiv={setDiv}
              user={user}
              setUser={setUser}
              inputValue={inputValue}
              setInputValue={setInputValue}
            />
          </Box>
        )}
        <Box
          sx={{
            width: `${
              sectionHeaderExpanded
                ? `calc(100vw - ${sizeConfigs.sidebar.width})`
                : "100vw"
            }`,
          }}
        >
          {sections.length > 0 && grid}
          <Box
            sx={{
              backgroundColor: "#d0d0d0",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Typography sx={{ color: "#7b7b7b", fontWeight: "bold", p: 1 }}>
              {sections.length}/{sections.length}
            </Typography>
          </Box>
        </Box>
      </Box>
    </div>
  );
}

export default React.memo(Sections);
